import { DatePartsPicker } from "@/components/hr/DatePartsPicker";
import { formatCurrency, getActiveCompanyCurrency } from "@/lib/currencies/currenciesStore";
import {
  PaymentMethodDetailFields,
  PaymentMethodSelect,
  applyPaymentMethodChange,
  emptyPaymentMethodDraft,
  type PaymentMethodDraft,
} from "@/components/sales/PaymentMethodFields";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { pkrAmountShortLabel } from "@/lib/formatPkrAmount";
import type { SaleBalancePaymentPayload } from "@/lib/sales/salesStore";
import { format } from "date-fns";
import { Loader2 } from "lucide-react";
import { useEffect, useState } from "react";

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  outstanding: number;
  onSubmit: (payload: SaleBalancePaymentPayload) => Promise<void>;
};

function todayIso(): string {
  return format(new Date(), "yyyy-MM-dd");
}

function parseAmount(raw: string): number | null {
  const trimmed = raw.trim().replace(/,/g, "");
  if (!trimmed) return null;
  const n = Number(trimmed);
  if (!Number.isFinite(n) || n <= 0) return null;
  return Math.round(n * 100) / 100;
}

function formatMoney(n: number): string {
  return n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

/** Records a lump-sum or partial balance payment on a non-installment sale. */
export function RecordBalancePaymentDialog({ open, onOpenChange, outstanding, onSubmit }: Props) {
  const [amountInput, setAmountInput] = useState("");
  const [paymentDate, setPaymentDate] = useState(todayIso());
  const [methodDraft, setMethodDraft] = useState<PaymentMethodDraft>(emptyPaymentMethodDraft());
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setAmountInput(outstanding > 0 ? String(outstanding) : "");
    setPaymentDate(todayIso());
    setMethodDraft(emptyPaymentMethodDraft());
    setNotes("");
    setSaving(false);
    setError(null);
  }, [open, outstanding]);

  const enteredAmount = parseAmount(amountInput) ?? 0;
  const enteredAmountLabel = pkrAmountShortLabel(enteredAmount);

  const save = async () => {
    if (saving) return;
    const amount = parseAmount(amountInput);
    if (amount == null) {
      setError("Enter a valid payment amount greater than zero.");
      return;
    }
    if (amount > outstanding + 0.009) {
      setError(`Payment cannot exceed the outstanding balance of ${formatMoney(outstanding)}.`);
      return;
    }
    if (!paymentDate.trim()) {
      setError("Payment date is required.");
      return;
    }

    setSaving(true);
    setError(null);
    try {
      await onSubmit({
        amount,
        payment_date: paymentDate,
        payment_method: methodDraft.paymentMethod || null,
        bank_name: methodDraft.bankName.trim() || null,
        transaction_reference_no: methodDraft.transactionReferenceNo.trim() || null,
        pay_order_no: methodDraft.payOrderNo.trim() || null,
        notes: notes.trim() || null,
      });
      onOpenChange(false);
    } catch {
      // Caller shows toast; keep dialog open for correction.
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg overflow-visible">
        <DialogHeader>
          <DialogTitle>Record balance payment</DialogTitle>
          <DialogDescription>
            {`${formatCurrency(outstanding)} outstanding. Record the full balance or a partial payment.`}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 overflow-visible py-1">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <div className="flex min-h-5 items-center justify-between gap-2">
                <Label htmlFor="balance-payment-amount">
                  Payment amount ({getActiveCompanyCurrency().code})
                </Label>
                {enteredAmountLabel ? (
                  <span className="shrink-0 text-xs font-semibold tabular-nums text-primary">
                    {enteredAmountLabel}
                  </span>
                ) : null}
              </div>
              <div className="relative flex items-center">
                <span className="absolute left-3 text-xs font-bold text-muted-foreground select-none pointer-events-none">
                  {getActiveCompanyCurrency().symbol}
                </span>
                <Input
                  id="balance-payment-amount"
                  type="text"
                  inputMode="decimal"
                  value={amountInput}
                  disabled={saving}
                  className="pl-8 glass-input"
                  onChange={(e) => {
                    const nextInput = e.target.value;
                    const nextAmount = parseAmount(nextInput);

                    if (nextAmount !== null && nextAmount > outstanding + 0.009) {
                      setError(`Maximum payment is ${formatCurrency(outstanding)}.`);
                      return;
                    }

                    setAmountInput(nextInput);
                    setError(null);
                  }}
                  placeholder="0.00"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Payment date</Label>
              <DatePartsPicker value={paymentDate} onChange={setPaymentDate} disabled={saving} />
            </div>
          </div>
          <p className="-mt-2 text-xs text-muted-foreground">
            Defaults to the full outstanding balance. Enter a smaller amount for a partial payment.
          </p>

          <PaymentMethodSelect
            id="balance-payment-method"
            value={methodDraft.paymentMethod}
            onChange={(method) => setMethodDraft((cur) => applyPaymentMethodChange(cur, method))}
            disabled={saving}
          />
          <PaymentMethodDetailFields
            method={methodDraft.paymentMethod}
            bankName={methodDraft.bankName}
            transactionReferenceNo={methodDraft.transactionReferenceNo}
            payOrderNo={methodDraft.payOrderNo}
            onBankNameChange={(v) => setMethodDraft((cur) => ({ ...cur, bankName: v }))}
            onTransactionReferenceChange={(v) => setMethodDraft((cur) => ({ ...cur, transactionReferenceNo: v }))}
            onPayOrderNoChange={(v) => setMethodDraft((cur) => ({ ...cur, payOrderNo: v }))}
            disabled={saving}
          />

          <div className="space-y-2">
            <Label htmlFor="balance-payment-notes">Notes</Label>
            <Textarea
              id="balance-payment-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Optional"
              rows={2}
              disabled={saving}
            />
          </div>

          {error ? <p className="text-sm text-destructive">{error}</p> : null}
        </div>

        <DialogFooter>
          <Button type="button" variant="glass" onClick={() => onOpenChange(false)} disabled={saving}>
            Cancel
          </Button>
          <Button type="button" variant="premium" onClick={() => void save()} disabled={saving}>
            {saving ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Saving…
              </>
            ) : (
              "Record payment"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
